import axios, { isAxiosError } from 'axios';
import {
  AdminUserInfo,
  AnewsStorageSearchParams,
  AnewsStorageSearchResult,
  BoxFolder,
  DeleteAnewsStorageFilesParams,
  DeleteAnewStorageFilesResult,
  IpAddressRestriction,
  MailDeliverySchedule,
  MSTeamsSettings,
  SamlFederation,
  SamlFederationLoginUrlResponse,
  SharepointCredential,
  SharepointSite,
  StorageType,
  UserDocumentContract,
  UserDocumentStorageStatus,
  UserDocumentUsage,
} from '@/types';
import { userSession } from '@/utils/userSession';

export async function fetchUsersForAdmin(): Promise<AdminUserInfo[]> {
  const myTeamId = userSession.getTeamId();
  const { data } = await axios.get<{ users: AdminUserInfo[] }>(
    `/admin/teams/${myTeamId}/users`,
  );
  return data.users;
}

export async function updateMSTeamsSettings(
  settings: MSTeamsSettings,
): Promise<MSTeamsSettings> {
  const myTeamId = userSession.getTeamId();
  const { data } = await axios.put<MSTeamsSettings>(
    `/admin/teams/${myTeamId}/ms_teams_settings`,
    settings,
  );
  return data;
}

export async function fetchMSTeamsSettings(): Promise<MSTeamsSettings> {
  const myTeamId = userSession.getTeamId();
  const { data } = await axios.get<MSTeamsSettings>(
    `/admin/teams/${myTeamId}/ms_teams_settings`,
  );
  return data;
}

// HTTPコードを返す
export async function headTeamBoxApiToken(): Promise<number> {
  const myTeamId = userSession.getTeamId();
  return await axios
    .head<void>(`/admin/teams/${myTeamId}/box_api_token`)
    .then(response => response.status)
    .catch(error => error.response.status);
}

export async function deleteTeamBoxApiToken(): Promise<void> {
  await axios.delete<void>(
    `/admin/teams/${userSession.getTeamId()}/box_api_token`,
  );
}

export async function getTeamStorageStatus(
  storageType: StorageType,
): Promise<UserDocumentStorageStatus> {
  const { data } = await axios.get<UserDocumentStorageStatus>(
    `/admin/teams/${userSession.getTeamId()}/storage_status`,
    {
      params: {
        storage_type: storageType,
      },
    },
  );
  return data;
}

export async function getTeamUserDocumentUsage(): Promise<UserDocumentUsage> {
  const { data } = await axios.get<UserDocumentUsage>(
    `/admin/teams/${userSession.getTeamId()}/user_document_usage`,
  );
  return data;
}

export async function getTeamActiveUserDocumentContract(): Promise<UserDocumentContract> {
  const { data } = await axios.get<UserDocumentContract>(
    `/admin/teams/${userSession.getTeamId()}/active_user_document_contract`,
  );
  return data;
}

// HTTPコードを返す
export async function headTeamSharepointCredential(): Promise<number> {
  const myTeamId = userSession.getTeamId();
  return await axios
    .head<void>(`/admin/teams/${myTeamId}/sharepoint_credential`)
    .then(response => response.status)
    .catch(error => error.response.status);
}

export async function getBoxFolders(
  datasource_enabled: boolean,
): Promise<BoxFolder[]> {
  const myTeamId = userSession.getTeamId();
  const { data } = await axios.get<BoxFolder[]>(
    `/admin/teams/${myTeamId}/box_folders`,
    {
      params: {
        datasource_enabled: datasource_enabled,
      },
    },
  );
  return data;
}

export async function postBoxFolders(boxFolders: BoxFolder[]): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.post<void>(`/admin/teams/${myTeamId}/box_folders/bulk_create`, {
    box_folders: boxFolders,
  });
}

export async function putBoxFolders(boxFolders: BoxFolder[]): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.put<void>(`/admin/teams/${myTeamId}/box_folders/bulk_update`, {
    box_folders: boxFolders,
  });
}

export async function getSharepointCredential(): Promise<
  SharepointCredential | undefined
> {
  try {
    const myTeamId = userSession.getTeamId();
    const { data } = await axios.get<SharepointCredential>(
      `/admin/teams/${myTeamId}/sharepoint_credential`,
    );
    return data;
  } catch (e) {
    // 未認証という正常系の場合は常に404エラーが出るので、これを大域例外へ飛ばすのは適切ではない
    // サーバ側はcredentialリソースがあるかどうかなので、404で返したい意図はわかるかつ影響範囲が大きそうなので
    // フロント側で吸収する
    // axios error ではなく、ステータスコードが404でなければ大域例外へ例外を投げる
    if (isAxiosError(e) && e.response?.status === 404) {
      return Promise.resolve(undefined);
    }
    throw e;
  }
}

export async function postSharepointCredential({
  clientId,
  tenantId,
  thumbprint,
  sharepointSites,
}: {
  clientId: string;
  tenantId: string;
  thumbprint: string;
  sharepointSites?: { site_url: string }[];
}): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.post<void>(`/admin/teams/${myTeamId}/sharepoint_credential`, {
    client_id: clientId,
    thumbprint: thumbprint,
    tenant_id: tenantId,
    sharepoint_sites: sharepointSites,
  });
}

export async function putSharepointCredential({
  clientId,
  tenantId,
  thumbprint,
}: {
  clientId?: string;
  tenantId?: string;
  thumbprint: string;
}): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.put<void>(`/admin/teams/${myTeamId}/sharepoint_credential`, {
    client_id: clientId,
    thumbprint: thumbprint,
    tenant_id: tenantId,
  });
}

export async function deleteSharepointCredential(): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.delete<void>(`/admin/teams/${myTeamId}/sharepoint_credential`);
}

export async function issueSharepointCertificate(): Promise<Blob> {
  const myTeamId = userSession.getTeamId();
  const { data } = await axios.post<Blob>(
    `/admin/teams/${myTeamId}/sharepoint_credential/issue_certificate`,
    {},
    {
      responseType: 'blob',
    },
  );
  return data;
}

export async function getSharepointSites(): Promise<SharepointSite[]> {
  const myTeamId = userSession.getTeamId();
  const { data } = await axios.get<SharepointSite[]>(
    `/admin/teams/${myTeamId}/sharepoint_sites`,
  );
  return data;
}

export async function postOneSharepointSite(siteUrl: string): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.post<void>(`/admin/teams/${myTeamId}/one_sharepoint_site`, {
    site_url: siteUrl,
  });
}

export async function putSharepointSites(
  sites: { site_url: string }[],
): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.put<void>(
    `/admin/teams/${myTeamId}/sharepoint_sites/bulk_update`,
    { sharepoint_sites: sites },
  );
}

// HTTPコードを返す
export async function headTeamAnewsStorageSyncHistory(): Promise<number> {
  const myTeamId = userSession.getTeamId();
  return await axios
    .head<void>(`/admin/teams/${myTeamId}/anews_storage_sync_history`)
    .then(response => response.status)
    .catch(error => error.response.status);
}

export async function deleteTeamAnewsStorageDatasource(): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.delete<void>(`/admin/teams/${myTeamId}/anews_storage_datasource`);
}

export async function postTeamAnewsStorageDatasource(): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.post<void>(`/admin/teams/${myTeamId}/anews_storage_datasource`);
}

export async function deleteManagementContractManagingUser(
  contractId: number,
  userId: number,
): Promise<void> {
  await axios.delete<void>(
    `/admin/management_contracts/${contractId}/managing_users/${userId}`,
  );
}

export async function addManagementContractManagingUsers(
  contractId: number,
  userIds: number[],
): Promise<void> {
  await axios.put<void>(
    `/admin/management_contracts/${contractId}/managing_users`,
    {
      user_ids: userIds,
    },
  );
}

export async function putEnableIpAddressRestriction(
  enableIPAddressRestriction: boolean,
) {
  const myTeamId = userSession.getTeamId();
  await axios.put<void>(
    `/admin/teams/${myTeamId}/enable_ip_address_restriction`,
    {
      enable_ip_address_restriction: enableIPAddressRestriction,
    },
  );
}

export async function putIpAddressRestriction(
  ipAddressRestriction: IpAddressRestriction,
): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.put<void>(
    `/admin/teams/${myTeamId}/ip_address_restrictions/${ipAddressRestriction.id}`,
    {
      ip_address: ipAddressRestriction.ip_address,
      description: ipAddressRestriction.description,
    },
  );
}

export async function deleteIpAddressRestriction(
  ipAddressRestrictionId: number,
): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.delete<void>(
    `/admin/teams/${myTeamId}/ip_address_restrictions/${ipAddressRestrictionId}`,
  );
}

export async function postIpAddressRestrictionBulkCreate(
  ipAddressRestrictions: IpAddressRestriction[],
): Promise<void> {
  const myTeamId = userSession.getTeamId();
  await axios.post<void>(
    `/admin/teams/${myTeamId}/ip_address_restrictions/bulk_create`,
    {
      ip_address_restrictions: ipAddressRestrictions,
    },
  );
}

export async function updateMailDeliverySchedules(
  schedules: MailDeliverySchedule[],
): Promise<void> {
  const teamId = userSession.getTeamId();
  await axios.put<void>(`/admin/teams/${teamId}/mail_delivery_schedules`, {
    mail_delivery_schedules: schedules,
  });
}

export async function getSamlFederationLoginUrl(
  metadataUrl: string,
): Promise<SamlFederationLoginUrlResponse> {
  const teamId = userSession.getTeamId();
  const response = await axios.get<SamlFederationLoginUrlResponse>(
    `/admin/teams/${teamId}/saml_federation/login_url?federation_metadata_url=${metadataUrl}`,
  );
  return response.data;
}

export async function updateSamlFederation({
  name,
  metadataUrl,
  activated,
}: {
  name?: string | null;
  metadataUrl?: string | null;
  activated?: boolean;
}): Promise<SamlFederation> {
  const teamId = userSession.getTeamId();
  const response = await axios.patch<SamlFederation>(
    `/admin/teams/${teamId}/saml_federation`,
    {
      name: name,
      federation_metadata_url: metadataUrl,
      activated,
    },
  );
  return response.data;
}

export async function checkExistsAnewsStorageFiles(
  file_names: string[],
): Promise<Record<string, boolean>> {
  const teamId = userSession.getTeamId();

  const response = await axios.post<Record<string, boolean>>(
    `/admin/teams/${teamId}/anews_storage_datasource/files/exists`,
    { file_names },
  );
  return response.data;
}

export async function getAnewsStorageFiles(
  params: AnewsStorageSearchParams = {},
): Promise<AnewsStorageSearchResult> {
  const teamId = userSession.getTeamId();
  const response = await axios.get<AnewsStorageSearchResult>(
    `/admin/teams/${teamId}/anews_storage_datasource/files`,
    { params },
  );
  return response.data;
}

export async function deleteAnewsStorageFile(
  params: DeleteAnewsStorageFilesParams,
): Promise<DeleteAnewStorageFilesResult> {
  const teamId = userSession.getTeamId();
  try {
    await axios.delete<DeleteAnewsStorageFilesParams>(
      `/admin/teams/${teamId}/anews_storage_datasource/files`,
      { params },
    );
    return Promise.resolve<DeleteAnewStorageFilesResult>('no_content');
  } catch (error) {
    // エラーコード409の場合は常に
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return Promise.resolve<DeleteAnewStorageFilesResult>('syncing');
    }
    return Promise.reject<DeleteAnewStorageFilesResult>(error);
  }
}
